import { environment } from '@utility/app.util';

import { isNil } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { AuthService } from '../auth/services/auth.service';
import { UserPermissions, UserRoles, UserRolesEnum } from '@app/app.permissions';

import {
  StatisticsDto,
  DefaultResponseDto,
  NotificationListDto,
  UserInfoResponseDto,
  NetworkStatsResponse,
  EngagementStatsNewDto,
  EngagementStatsResponseDto,
  EngagementStatsUsergroupsResponseDto,
  AdaptionRateDashboardStatsResponseDto,
  EngagementStatsDepartmentsResponseDto,
} from '@sociuu/interfaces';
import { IPostFrequency, IServerPostFrequency, IServerPostFrequencyData } from '@lib/dashboard.interface';

import { initQueryParams } from '@app/utility/app.util';
import { setSentryUser } from '@app/utility/sentry.utils';
import { setSentryContext } from '@app/utility/sentry-error-handlers.utils';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  constructor(
    private router: Router,
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  public getNotificationList(): Observable<NotificationListDto> {
    return this.http.get<NotificationListDto>(`${environment.apiUrl}/notifications/list`);
  }

  public markNotificationAsRead(client_notification_id: number): Observable<DefaultResponseDto> {
    return this.http.put<DefaultResponseDto>(`${environment.apiUrl}/notifications/mark-as-read/${client_notification_id}`, ``);
  }

  public getStats(payload, with_posts = 'no'): Observable<StatisticsDto> {
    let params = new HttpParams().append('with_posts', with_posts);
    if (payload.date_to && payload.date_to !== '-1') params = params.append('date_to', payload.date_to);
    if (payload.date_from && payload.date_from !== '-1') params = params.append('date_from', payload.date_from);

    return this.http.get<StatisticsDto>(`${environment.apiUrl}/statistics`, { params });
  }

  public getDashboardStats(payload, with_posts = 'no'): Observable<StatisticsDto> {
    let params = new HttpParams().append('with_posts', with_posts);
    if (payload.date_to && payload.date_to !== '-1') params = params.append('date_to', payload.date_to);
    if (payload.date_from && payload.date_from !== '-1') params = params.append('date_from', payload.date_from);

    return this.http.get<StatisticsDto>(`${environment.apiUrl}/v2/statistics`, { params });
  }

  public getNetworkStats(payload): Observable<NetworkStatsResponse> {
    let params = new HttpParams();
    if (payload.date_to && payload.date_to !== '-1') params = params.append('date_to', payload.date_to);
    if (payload.date_from && payload.date_from !== '-1') params = params.append('date_from', payload.date_from);
    
    return this.http.get<NetworkStatsResponse>(`${environment.apiUrl}/v2/statistics/network-stats`, { params });
  }

  public getNewEngagementStats(payload): Observable<EngagementStatsResponseDto> {
    let params = new HttpParams();
    if (payload.groupBy) params = params.append('groupBy', payload.groupBy);
    if (payload.date_to && payload.date_to !== '-1') params = params.append('date_to', payload.date_to);
    if (payload.date_from && payload.date_from !== '-1') params = params.append('date_from', payload.date_from);

    return this.http.get<EngagementStatsResponseDto>(`${environment.apiUrl}/v2/engagement`, { params });
  }

  public getEngagementUsergroupsStats(payload, existingParams?: HttpParams): Observable<EngagementStatsUsergroupsResponseDto> {
    let params: HttpParams = existingParams ? existingParams : new HttpParams();
    params = params.append('groupBy', payload.groupBy);
    params = params.append('with_stats', 'true');
    params = params.append('metrics[]', 'invites');
    params = params.append('metrics[]', 'unique_sharers');
    params = params.append('metrics[]', 'engagement_rate');
    // The engagement rate divides by the people invited; the API only returns them when asked.
    if (!params.getAll('metrics[]')?.includes('unique_invites')) {
      params = params.append('metrics[]', 'unique_invites');
    }
    if (payload.date_to && payload.date_to !== '-1') params = params.append('date_to', payload.date_to);
    if (payload.date_from && payload.date_from !== '-1') params = params.append('date_from', payload.date_from);

    return this.http.get<EngagementStatsUsergroupsResponseDto>(`${environment.apiUrl}/v2/usergroups`, { params });
  }

  public getPostFrequencyDashboardStats(payload): Observable<IPostFrequency[]> {
    let params = new HttpParams().append('groupBy', payload.groupBy);
    if (payload.date_to && payload.date_to !== '-1') params = params.append('date_to', payload.date_to);
    if (payload.date_from && payload.date_from !== '-1') params = params.append('date_from', payload.date_from);

    return this.http.get<IServerPostFrequency>(`${environment.apiUrl}/v2/statistics/post-frequency`, { params }).pipe(
      map((response: IServerPostFrequency): IPostFrequency[] => {
        return response.data.map((item: IServerPostFrequencyData): IPostFrequency => this.transformPostFrequency(item));
      }),
    );
  }

  public getAdoptionRateDashboardStats(payload): Observable<AdaptionRateDashboardStatsResponseDto> {
    let params = new HttpParams().append('groupBy', payload.groupBy);
    if (payload.date_to && payload.date_to !== '-1') params = params.append('date_to', payload.date_to);
    if (payload.date_from && payload.date_from !== '-1') params = params.append('date_from', payload.date_from);

    return this.http.get<AdaptionRateDashboardStatsResponseDto>(`${environment.apiUrl}/v2/statistics/adaption-rate`, { params });
  }

  public getUserProfile(): Observable<UserInfoResponseDto> {
    return this.http.get<UserInfoResponseDto>(`${environment.apiUrl}/users/profile`).pipe(
      tap((res) => {
        this.authService.currentUser.next(res.data);
        this.assigneeUserRole(res?.data?.roles?.slug);
        this.mapUserPermissions([...(res?.data?.roles?.permissions || [])]);
        setSentryUser({
          id: res?.data?.id,
          email: res?.data?.email,
          username: `${res?.data?.first_name} ${res?.data?.last_name}`,
        });

        // Set Sentry context for error reporting with user information
        // Note: companyName is set separately by getClientInfo() since it's not in the profile response
        setSentryContext({
          userId: res?.data?.id,
          userName: `${res?.data?.first_name || ''} ${res?.data?.last_name || ''}`.trim(),
          userEmail: res?.data?.email,
          companyId: res?.data?.client_id,
        });
      }),
      catchError(() => {
        this.router.navigate(['/login'], initQueryParams(this.router));
        return of(null);
      }),
    );
  }

  public getEngagementDepartmentsStats(payload): Observable<EngagementStatsDepartmentsResponseDto> {
    let params = new HttpParams().append('groupBy', payload.groupBy);
    if (payload.date_to && payload.date_to !== '-1') params = params.append('date_to', payload.date_to);
    if (payload.date_from && payload.date_from !== '-1') params = params.append('date_from', payload.date_from);

    return this.http.get<EngagementStatsDepartmentsResponseDto>(`${environment.apiUrl}/v2/engagement_stats/departments`, {
      params,
    });
  }

  public getEngagementStatsNew(filterData): Observable<EngagementStatsNewDto> {
    let params = new HttpParams();

    if (filterData.pid !== null && filterData.pid !== '') {
      params = params.append('pid', filterData.pid);
    } else {
      if ((filterData.ugid !== null) !== null && filterData.ugid !== '') {
        params = params.append('ugid', filterData.ugid);
      }
      if (filterData.date_to && filterData.date_to !== '-1') params = params.append('date_to', filterData.date_to);
      if (filterData.date_from && filterData.date_from !== '-1') params = params.append('date_from', filterData.date_from);
    }

    if (filterData.export) {
      params = params.append('export', filterData.export);
    }
    return this.http.get<EngagementStatsNewDto>(`${environment.apiUrl}/engagement_stats_new`, { params }).pipe(shareReplay(1));
  }

  private mapUserPermissions(permissions: any[]): void {
    permissions.forEach((permission) => {
      if (isNil(UserPermissions[permission.slug])) {
        console.warn('New permission found', permission.slug);
      }
      UserPermissions[permission.slug] = true;
    });
  }

  private assigneeUserRole(currentRole: UserRolesEnum | string): void {
    UserRoles[currentRole] = true;
  }

  private transformPostFrequency(response: IServerPostFrequencyData): IPostFrequency {
    return {
      fullDate: response.full_date,
      postsSent: response.total_campaigns,
      xValue: response.x_value,
    };
  }
}
